import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { useIsMobileViewport } from "../../hooks/useIsMobileViewport";

import type { NavEntry } from "./YaasSidebar";
import { YaasSidebar } from "./YaasSidebar";
import type { YaasConsoleKind, YaasTopBarUser } from "./YaasTopBar";
import { YaasTopBar } from "./YaasTopBar";

interface YaasShellProps {
    navItems: NavEntry[];
    /** Pinned to the sidebar bottom block, below Collapse, above user. */
    bottomNavItems?: NavEntry[];
    /** Optional non-navigation content rendered between main nav and footer. */
    sidebarAccessory?: React.ReactNode;
    /** Client or issuer console; drives the top-bar kind chip. */
    consoleKind: YaasConsoleKind;
    appLabel: string;
    children: React.ReactNode;
    user?: YaasTopBarUser;
    onLogout?: () => void;
    /** Issuer-only toolbar actions rendered in the shared top bar. */
    topBarActions?: React.ReactNode;
    /**
     * Optional override for the top bar. When provided, replaces the
     * default `<YaasTopBar>` and receives the live sidebar `collapsed`
     * state so a custom header can keep its logo block aligned with the
     * nav rail, plus the mobile drawer toggle (`onMenuClick` — defined
     * only on mobile — and `menuOpen`) so a custom header can render its
     * own hamburger. Used by the client console's classic header.
     */
    renderTopBar?: (args: {
        collapsed: boolean;
        onMenuClick?: () => void;
        menuOpen: boolean;
    }) => React.ReactNode;
    /** Initial collapsed state for the sidebar. Defaults to expanded. */
    initialSidebarCollapsed?: boolean;
    /** Tailwind width class for the expanded sidebar rail (default `w-40`). */
    expandedSidebarWidthClass?: string;
    /**
     * Viewport width (px) below which the sidebar force-collapses to its icon
     * rail, regardless of the user's manual toggle. Above it, the manual
     * toggle applies. Omit to disable auto-collapse entirely (the issuer
     * console does). The client console sets this to the point where its detail
     * panel would drop below the `@xl` grid→stack width.
     */
    autoCollapseSidebarBelow?: number;
    /**
     * Which side the mobile nav drawer enters from. `left`/`right` are
     * fixed-width side rails; `bottom` is a full-width sheet that slides up.
     * Defaults to `left` (hamburger at the start of the bar). The client
     * console uses `bottom`.
     */
    drawerSide?: "left" | "right" | "bottom";
}

export function YaasShell({
    navItems,
    bottomNavItems,
    sidebarAccessory,
    consoleKind,
    appLabel,
    children,
    user,
    onLogout,
    topBarActions,
    renderTopBar,
    initialSidebarCollapsed = false,
    expandedSidebarWidthClass,
    autoCollapseSidebarBelow,
    drawerSide = "left"
}: YaasShellProps): React.ReactElement {
    const [userCollapsed, setUserCollapsed] = useState(initialSidebarCollapsed);
    const isMobile = useIsMobileViewport();
    // Below `autoCollapseSidebarBelow` the rail is forced to its icon state so
    // the content area keeps its width; the user's manual toggle only applies
    // above that point. `?? 0` → `(max-width: -1px)`, which never matches, so
    // an unset prop disables auto-collapse.
    const autoCollapsed = useIsMobileViewport(autoCollapseSidebarBelow ?? 0);
    const collapsed = userCollapsed || autoCollapsed;
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const { pathname } = useLocation();

    // Mobile only: the hamburger toggle, shared by the default top bar and any
    // custom `renderTopBar` header. Undefined on desktop (no drawer).
    const onMenuClick = isMobile
        ? (): void => setMobileNavOpen((o) => !o)
        : undefined;
    const isBottomDrawer = drawerSide === "bottom";
    const isRightDrawer = drawerSide === "right";
    // Off-canvas resting offset + anchoring per side. The bottom variant is a
    // full-width sheet that slides up; left/right are fixed-width side rails.
    const drawerOffscreen = isBottomDrawer
        ? { y: "100%" }
        : { x: isRightDrawer ? "100%" : "-100%" };
    const drawerOnscreen = isBottomDrawer ? { y: 0 } : { x: 0 };
    const drawerAnchorClass = isBottomDrawer
        ? "inset-x-0 bottom-0 top-11"
        : `bottom-0 top-11 w-64 max-w-[80vw] ${
              isRightDrawer ? "right-0" : "left-0"
          }`;

    // Navigating (tapping a nav row) should dismiss the drawer.
    useEffect(() => {
        setMobileNavOpen(false);
    }, [pathname]);

    // If the viewport grows back to desktop, the drawer's open state is
    // meaningless — drop it so it can't linger behind the in-flow rail.
    useEffect(() => {
        if (!isMobile) setMobileNavOpen(false);
    }, [isMobile]);

    return (
        <div className="h-dvh flex flex-col bg-surface-ground overflow-hidden">
            {renderTopBar ? (
                renderTopBar({
                    collapsed,
                    onMenuClick,
                    menuOpen: mobileNavOpen
                })
            ) : (
                <YaasTopBar
                    consoleKind={consoleKind}
                    appLabel={appLabel}
                    user={user}
                    sidebarCollapsed={collapsed}
                    actions={topBarActions}
                    onMenuClick={onMenuClick}
                    menuOpen={mobileNavOpen}
                />
            )}

            {/* Both flex items must be allowed to shrink below their content's
                intrinsic height. Without min-h-0, a long page can enlarge this
                row past the h-dvh shell and create a second document scroll. */}
            <div className="flex min-h-0 flex-1 overflow-hidden">
                {/* Desktop: in-flow collapsible rail. On mobile the rail is
                    replaced by the off-canvas drawer below, so `<main>`
                    spans the full viewport width (no horizontal scroll). */}
                {!isMobile && (
                    <YaasSidebar
                        navItems={navItems}
                        bottomNavItems={bottomNavItems}
                        sidebarAccessory={sidebarAccessory}
                        user={user}
                        onLogout={onLogout}
                        collapsed={collapsed}
                        onToggleCollapsed={() => setUserCollapsed((c) => !c)}
                        expandedWidthClass={expandedSidebarWidthClass}
                    />
                )}

                <main className="min-h-0 min-w-0 flex-1 overflow-auto overscroll-contain">
                    {children}
                </main>
            </div>

            {/* ── Mobile off-canvas nav drawer ─────────────────────────
                Sits below the top bar (`top-11`) so the hamburger stays
                visible to close it. Scrim on z-40 (backdrop tier), drawer
                on z-50 (floater tier), per the shared z-index scale. */}
            <AnimatePresence>
                {isMobile && mobileNavOpen && (
                    <React.Fragment>
                        <motion.div
                            key="nav-scrim"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => setMobileNavOpen(false)}
                            aria-hidden="true"
                            className="fixed inset-x-0 bottom-0 top-11 z-40 bg-surface-dialog-base supports-backdrop-filter:backdrop-blur-sm md:hidden"
                        />
                        <motion.div
                            key="nav-drawer"
                            initial={drawerOffscreen}
                            animate={drawerOnscreen}
                            exit={drawerOffscreen}
                            transition={{
                                type: "tween",
                                ease: [0.16, 1, 0.3, 1],
                                duration: 0.25
                            }}
                            className={`fixed z-50 md:hidden ${drawerAnchorClass}`}
                        >
                            <YaasSidebar
                                variant="drawer"
                                drawerSide={drawerSide}
                                navItems={navItems}
                                bottomNavItems={bottomNavItems}
                                sidebarAccessory={sidebarAccessory}
                                user={user}
                                onLogout={onLogout}
                                onNavigate={() => setMobileNavOpen(false)}
                            />
                        </motion.div>
                    </React.Fragment>
                )}
            </AnimatePresence>
        </div>
    );
}
